import pick from 'lodash/pick';
import moment from 'moment';
import queryString from 'query-string';
import config from '../../config';
import { types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { trackIntercomEvent } from '../../util/intercom';
import { addMarketplaceEntities, getListingsById } from '../../ducks/marketplaceData.duck';
import {
  transactionLineItems,
  projectInitiatePrivileged,
  fetchListingApplicationCount,
} from '../../util/api';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import { TRANSITION_MAKE_PROJECT_BID } from '../../util/transaction';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import { fetchCurrentUser, fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/ProjectListingPage/SET_INITIAL_VALUES';

export const SHOW_PROJECT_REQUEST = 'app/ProjectListingPage/SHOW_PROJECT_REQUEST';
export const SHOW_PROJECT_ERROR = 'app/ProjectListingPage/SHOW_PROJECT_ERROR';

export const FETCH_REVIEWS_REQUEST = 'app/ProjectListingPage/FETCH_REVIEWS_REQUEST';
export const FETCH_REVIEWS_SUCCESS = 'app/ProjectListingPage/FETCH_REVIEWS_SUCCESS';
export const FETCH_REVIEWS_ERROR = 'app/ProjectListingPage/FETCH_REVIEWS_ERROR';

export const FETCH_TIME_SLOTS_REQUEST = 'app/ProjectListingPage/FETCH_TIME_SLOTS_REQUEST';
export const FETCH_TIME_SLOTS_SUCCESS = 'app/ProjectListingPage/FETCH_TIME_SLOTS_SUCCESS';
export const FETCH_TIME_SLOTS_ERROR = 'app/ProjectListingPage/FETCH_TIME_SLOTS_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/ProjectListingPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/ProjectListingPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/ProjectListingPage/FETCH_LINE_ITEMS_ERROR';

export const SEND_PROJECT_BID_REQUEST = 'app/ProjectListingPage/SEND_PROJECT_BID_REQUEST';
export const SEND_PROJECT_BID_SUCCESS = 'app/ProjectListingPage/SEND_PROJECT_BID_SUCCESS';
export const SEND_PROJECT_BID_ERROR = 'app/ProjectListingPage/SEND_PROJECT_BID_ERROR';

export const FETCH_APPLICATION_COUNT_REQUEST =
  'app/ProjectListingPage/FETCH_APPLICATION_COUNT_REQUEST';
export const FETCH_APPLICATION_COUNT_SUCCESS =
  'app/ProjectListingPage/FETCH_APPLICATION_COUNT_SUCCESS';
export const FETCH_APPLICATION_COUNT_ERROR = 'app/ProjectListingPage/FETCH_APPLICATION_COUNT_ERROR';

export const CHECK_USER_EXISTING_PROPOSAL_REQUEST =
  'app/ProjectListingPage/CHECK_USER_EXISTING_PROPOSAL_REQUEST';
export const CHECK_USER_EXISTING_PROPOSAL_SUCCESS =
  'app/ProjectListingPage/CHECK_USER_EXISTING_PROPOSAL_SUCCESS';
export const CHECK_USER_EXISTING_PROPOSAL_ERROR =
  'app/ProjectListingPage/CHECK_USER_EXISTING_PROPOSAL_ERROR';

// ================ Reducer ================ //

const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  timeSlots: null,
  fetchTimeSlotsError: null,
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  pricingPackage: null,
  suggestedPrice: null,
  sendProjectBidInProgress: false,
  sendProjectBidError: null,
  checkExistingProposalInProgress: false,
  hasExistingProposal: false,
  existingProposalTransactionId: null,
  existingProposalError: null,
  applicationCount: null,
  fetchApplicationCountError: null,
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SHOW_PROJECT_REQUEST:
      return { ...state, id: payload.id, showListingError: null };
    case SHOW_PROJECT_ERROR:
      return { ...state, showListingError: payload };

    case FETCH_REVIEWS_REQUEST:
      return { ...state, fetchReviewsError: null };
    case FETCH_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case FETCH_REVIEWS_ERROR:
      return { ...state, fetchReviewsError: payload };

    case FETCH_TIME_SLOTS_REQUEST:
      return { ...state, fetchTimeSlotsError: null };
    case FETCH_TIME_SLOTS_SUCCESS:
      return { ...state, timeSlots: payload };
    case FETCH_TIME_SLOTS_ERROR:
      return { ...state, fetchTimeSlotsError: payload };

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case SEND_PROJECT_BID_REQUEST:
      return { ...state, sendProjectBidInProgress: true, sendProjectBidError: null };
    case SEND_PROJECT_BID_SUCCESS:
      return { ...state, sendProjectBidInProgress: false };
    case SEND_PROJECT_BID_ERROR:
      return { ...state, sendProjectBidInProgress: false, sendProjectBidError: payload };

    case FETCH_APPLICATION_COUNT_REQUEST:
      return { ...state, fetchApplicationCountError: null };
    case FETCH_APPLICATION_COUNT_SUCCESS:
      return { ...state, applicationCount: payload };
    case FETCH_APPLICATION_COUNT_ERROR:
      return { ...state, fetchApplicationCountError: payload };

    case CHECK_USER_EXISTING_PROPOSAL_REQUEST:
      return { ...state, checkExistingProposalInProgress: true, existingProposalError: null };
    case CHECK_USER_EXISTING_PROPOSAL_SUCCESS:
      return {
        ...state,
        checkExistingProposalInProgress: false,
        hasExistingProposal: !!payload,
        existingProposalTransactionId: payload,
      };
    case CHECK_USER_EXISTING_PROPOSAL_ERROR:
      return { ...state, checkExistingProposalInProgress: false, existingProposalError: payload };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const showListingRequest = id => ({
  type: SHOW_PROJECT_REQUEST,
  payload: { id },
});

export const showListingError = e => ({
  type: SHOW_PROJECT_ERROR,
  error: true,
  payload: e,
});

export const fetchReviewsRequest = () => ({ type: FETCH_REVIEWS_REQUEST });
export const fetchReviewsSuccess = reviews => ({ type: FETCH_REVIEWS_SUCCESS, payload: reviews });
export const fetchReviewsError = error => ({
  type: FETCH_REVIEWS_ERROR,
  error: true,
  payload: error,
});

export const fetchTimeSlotsRequest = () => ({ type: FETCH_TIME_SLOTS_REQUEST });
export const fetchTimeSlotsSuccess = timeSlots => ({
  type: FETCH_TIME_SLOTS_SUCCESS,
  payload: timeSlots,
});
export const fetchTimeSlotsError = error => ({
  type: FETCH_TIME_SLOTS_ERROR,
  error: true,
  payload: error,
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

export const sendProjectBidRequest = () => ({ type: SEND_PROJECT_BID_REQUEST });
export const sendProjectBidSuccess = () => ({ type: SEND_PROJECT_BID_SUCCESS });
export const sendProjectBidError = e => ({ type: SEND_PROJECT_BID_ERROR, error: true, payload: e });

export const fetchApplicationCountRequest = () => ({ type: FETCH_APPLICATION_COUNT_REQUEST });
export const fetchApplicationCountSuccess = count => ({
  type: FETCH_APPLICATION_COUNT_SUCCESS,
  payload: count,
});
export const fetchApplicationCountError = e => ({
  type: FETCH_APPLICATION_COUNT_ERROR,
  error: true,
  payload: e,
});

export const checkUserExistingProposalRequest = () => ({
  type: CHECK_USER_EXISTING_PROPOSAL_REQUEST,
});
export const checkUserExistingProposalSuccess = transactionId => ({
  type: CHECK_USER_EXISTING_PROPOSAL_SUCCESS,
  payload: transactionId,
});
export const checkUserExistingProposalError = e => ({
  type: CHECK_USER_EXISTING_PROPOSAL_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const showListing = (listingId, isOwn = false, searchParams) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(showListingRequest(listingId));
  dispatch(fetchCurrentUser());
  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images'],
    'fields.image': [
      // Listing page
      'variants.landscape-crop',
      'variants.landscape-crop2x',
      'variants.landscape-crop4x',
      'variants.landscape-crop6x',

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Image carousel
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  return show
    .then(data => {
      dispatch(addMarketplaceEntities(data));
      return data;
    })
    .then(response => {
      const listings = getListingsById(getState(), [getState().ProjectListingPage.id]);

      if (Array.isArray(listings) && listings.length > 0) {
        const listing = listings[0];

        if (typeof window === 'object') {
          window.dataLayer.push({
            event: 'view_item',
            ecommerce: {
              items: [
                {
                  item_name: listing.attributes.title,
                  item_id: listing.id.uuid,
                  price: '' + listing.attributes.price.amount / 100,
                  item_brand: listing.author.attributes.profile.displayName,
                  item_category: listing.attributes.publicData.category,
                  item_list_name: 'search_results',
                  quantity: '1',
                },
              ],
            },
          });
        }
      }

      return response;
    })
    .catch(e => {
      console.log(e);
      dispatch(showListingError(storableError(e)));
    });
};

export const fetchReviews = listingId => (dispatch, getState, sdk) => {
  dispatch(fetchReviewsRequest());

  return sdk.listings
    .show({ id: listingId, include: ['author'] })
    .then(response => {
      const authorId = response.data.data.relationships.author.data.id;

      return sdk.reviews.query({
        subject_id: authorId,
        state: 'public',
        include: ['author', 'author.profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      });
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      dispatch(fetchReviewsSuccess(reviews));
    })
    .catch(e => {
      dispatch(fetchReviewsError(storableError(e)));
    });
};

const timeSlotsRequest = params => (dispatch, getState, sdk) => {
  return sdk.timeslots.query(params).then(response => {
    return denormalisedResponseEntities(response);
  });
};

export const fetchTimeSlots = listingId => (dispatch, getState, sdk) => {
  dispatch(fetchTimeSlotsRequest);

  // Time slots can be fetched for 90 days at a time,
  // for at most 180 days from now. If max number of bookable
  // day exceeds 90, a second request is made.

  const maxTimeSlots = 90;
  // booking range: today + bookable days -1
  const bookingRange = config.dayCountAvailableForBooking - 1;
  const timeSlotsRange = Math.min(bookingRange, maxTimeSlots);

  const start = moment
    .utc()
    .startOf('day')
    .toDate();
  const end = moment()
    .utc()
    .startOf('day')
    .add(timeSlotsRange, 'days')
    .toDate();
  const params = { listingId, start, end };

  return dispatch(timeSlotsRequest(params))
    .then(timeSlots => {
      const secondRequest = bookingRange > maxTimeSlots;

      if (secondRequest) {
        const secondRange = Math.min(maxTimeSlots, bookingRange - maxTimeSlots);
        const secondParams = {
          listingId,
          start: end,
          end: moment(end)
            .add(secondRange, 'days')
            .toDate(),
        };

        return dispatch(timeSlotsRequest(secondParams)).then(secondBatch => {
          const combined = timeSlots.concat(secondBatch);
          dispatch(fetchTimeSlotsSuccess(combined));
        });
      } else {
        dispatch(fetchTimeSlotsSuccess(timeSlots));
      }
    })
    .catch(e => {
      dispatch(fetchTimeSlotsError(storableError(e)));
    });
};

// Fetches only the aggregate number of applications for the listing from the
// local API — applicant identities are never exposed to the client.
export const fetchApplicationCount = listingId => dispatch => {
  dispatch(fetchApplicationCountRequest());

  return fetchListingApplicationCount(listingId.uuid)
    .then(response => {
      dispatch(fetchApplicationCountSuccess(response.count));
    })
    .catch(e => {
      dispatch(fetchApplicationCountError(storableError(e)));
    });
};

export const checkUserExistingProposal = (listingId, currentUser) => (dispatch, getState, sdk) => {
  // Don't check if user is not logged in
  if (!currentUser || !currentUser.id) {
    return Promise.resolve();
  }

  dispatch(checkUserExistingProposalRequest());

  // Query user's transactions where they are the customer (order side)
  // and include the listing relationship to check listing ID
  return sdk.transactions
    .query({
      only: 'order',
      processName: 'flex-project-process',
      include: ['listing'],
      per_page: 100, // Adjust if user might have more transactions
    })
    .then(response => {
      const transactions = response.data.data;

      // Find transaction for the current listing that uses the project process
      const existingTransaction = transactions.find(tx => {
        const txListingId = tx.relationships?.listing?.data?.id;
        const processName = tx.attributes?.processName;

        return txListingId && txListingId.uuid === listingId.uuid;
      });

      const transactionId = existingTransaction ? existingTransaction.id.uuid : null;
      dispatch(checkUserExistingProposalSuccess(transactionId));
      return transactionId;
    })
    .catch(e => {
      dispatch(checkUserExistingProposalError(storableError(e)));
      // Don't block UI on error, just log it
      console.error('Error checking existing proposal:', e);
      return false;
    });
};

export const sendProjectBid = (listingId, description, amount) => (dispatch, getState, sdk) => {
  dispatch(sendProjectBidRequest());
  const bodyParams = {
    transition: TRANSITION_MAKE_PROJECT_BID,
    processAlias: config.projectProcessAlias,
    params: {
      listingId,
      protectedData: {
        projectBidAmount: amount,
        projectBidDescription: description,
      },
    },
  };

  return projectInitiatePrivileged({
    isSpeculative: false,
    bookingData: { description, amount },
    bodyParams,
    queryParams: {},
  })
    .then(response => {
      const transactionId = response.data.data.id;

      // Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: description }).then(() => {
        dispatch(fetchCurrentUserHasOrdersSuccess(true));
        return transactionId;
      });
    })
    .then(response => {
      dispatch(sendProjectBidSuccess());
      dispatch(fetchCurrentUserHasOrdersSuccess(true));

      return response;
    })
    .catch(e => {
      dispatch(sendProjectBidError(storableError(e)));
      throw e;
    });
};

export const fetchTransactionLineItems = ({ bookingData, listingId, isOwnListing }) => dispatch => {
  dispatch(fetchLineItemsRequest());
  transactionLineItems({ bookingData, listingId, isOwnListing })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        bookingData: bookingData,
      });
    });
};

export const loadData = (params, search) => (dispatch, getState) => {
  const listingId = new UUID(params.id);
  const searchParams = queryString.parse(search);
  const state = getState();
  const { currentUser } = state.user;

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(listingId, true));
  }

  // Check if user has existing proposal for this listing
  const checkProposalPromise = currentUser
    ? dispatch(checkUserExistingProposal(listingId, currentUser))
    : Promise.resolve();

  if (config.enableAvailability) {
    return Promise.all([
      dispatch(showListing(listingId, false, searchParams)),
      dispatch(fetchTimeSlots(listingId)),
      dispatch(fetchReviews(listingId)),
      dispatch(fetchApplicationCount(listingId)),
      checkProposalPromise,
    ]);
  } else {
    return Promise.all([
      dispatch(showListing(listingId)),
      dispatch(fetchReviews(listingId)),
      dispatch(fetchApplicationCount(listingId)),
      checkProposalPromise,
    ]);
  }
};
